import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  PrimaryActionButton,
  SecondaryActionButton,
  StatePanel,
  StonePanel,
  Modal,
} from '@aztec-realms/ui';

import {
  useExpeditionCommand,
  useExpeditionChallenge,
  type ExpeditionCommand,
  type ExpeditionChallengeCommand,
} from './data/tribe-expedition-api';
import { selectCivicDecodeSession } from './data/api-vertical-slice';
import { CivicExpeditionChoices } from './CivicExpeditionChoices';
import { commandId, verticalSliceQueryKey } from './data/use-vertical-slice';
import { useCivicExpeditionBoard } from './CivicExpeditionProvider';
import './civic-expedition.css';

interface Props {
  tribeId: string;
  locked: boolean;
  safetyLocked: boolean;
}

const personalGoalLabels: Readonly<Record<string, string>> = {
  '/codex': 'Open your Codex',
  '/decode': 'Continue your glyph',
  '/explore': 'Explore your next discovery',
  '/solo': 'View your solo path',
};

export function CivicExpeditionPanel(props: Props) {
  const board = useCivicExpeditionBoard();
  return (
    <ExpeditionPanelSurface
      key={`${props.tribeId}:${board?.enabled}:${props.safetyLocked}`}
      {...props}
    />
  );
}

function ExpeditionPanelSurface(props: Props) {
  const board = useCivicExpeditionBoard();
  const [open, setOpen] = useState(false);
  if (!board) return null;
  // Legacy fixture contracts keep their established presentation; authored families use a sheet.
  if (!board.data?.choices) return <ExpeditionDetails {...props} />;
  return (
    <section className="civic-expedition civic-expedition-entry">
      <div>
        <h2 id="civic-expedition-title" tabIndex={-1}>
          Civic Expedition
        </h2>
        <p>{board.presentation.current?.branch_title ?? 'Choose what your Tribe restores next.'}</p>
      </div>
      <SecondaryActionButton disabled={props.safetyLocked} onClick={() => setOpen(true)}>
        {board.presentation.current?.status === 'active'
          ? 'Expedition details'
          : 'Choose an expedition'}
      </SecondaryActionButton>
      {open &&
        !props.safetyLocked &&
        createPortal(
          <div className="civic-expedition-overlay">
            <Modal open title="Your civic expedition" onClose={() => setOpen(false)}>
              <div className="civic-expedition-overlay__content">
                <ExpeditionDetails {...props} inOverlay />
              </div>
              <footer>
                <SecondaryActionButton onClick={() => setOpen(false)}>
                  Return to the city
                </SecondaryActionButton>
              </footer>
            </Modal>
          </div>,
          document.querySelector('.ar-game-shell') ?? document.body,
        )}
    </section>
  );
}

function ExpeditionDetails({
  tribeId,
  locked,
  safetyLocked,
  inOverlay = false,
}: Props & { inOverlay?: boolean }) {
  const board = useCivicExpeditionBoard();
  const command = useExpeditionCommand(tribeId);
  const challenge = useExpeditionChallenge(tribeId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pendingChallenge = useRef<ExpeditionChallengeCommand | null>(null);
  const [choiceFocus, setChoiceFocus] = useState<string>();
  // An ambiguous network result must reuse its original command, not contribute twice.
  const pending = useRef<ExpeditionCommand | null>(null);
  const submit = (action: ExpeditionCommand) => {
    if (
      !board?.enabled ||
      board.error ||
      locked ||
      safetyLocked ||
      command.isPending ||
      challenge.isPending
    )
      return;
    pending.current = action;
    command.mutate(action, {
      onSuccess: () => {
        pending.current = null;
      },
    });
  };
  const launch = (action: ExpeditionChallengeCommand) => {
    if (
      !board?.enabled ||
      board.error ||
      locked ||
      safetyLocked ||
      command.isPending ||
      challenge.isPending
    )
      return;
    pendingChallenge.current = action;
    challenge.mutate(action, {
      onSuccess: (session) => {
        pendingChallenge.current = null;
        selectCivicDecodeSession(session.id);
        navigate('/decode');
        void queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey });
      },
    });
  };
  if (!board) return null;
  const { current, completedRoles, history } = board.presentation;
  const active = current?.status === 'active';
  const disabled =
    !board.enabled ||
    locked ||
    safetyLocked ||
    command.isPending ||
    challenge.isPending ||
    Boolean(command.error) ||
    Boolean(challenge.error);
  const nextRole =
    active && current?.enrolled
      ? (current.workstreams.find((role) => role.current < role.target && role.can_contribute) ??
        current.workstreams.find((role) => role.current < role.target && role.can_launch))
      : undefined;

  return (
    <section
      aria-labelledby={inOverlay ? 'civic-expedition-detail-title' : 'civic-expedition-title'}
      className="civic-expedition"
    >
      <header>
        <span className="realm-screen__eyebrow">Together · Explore → Decode → Build</span>
        <h2
          id={inOverlay ? 'civic-expedition-detail-title' : 'civic-expedition-title'}
          tabIndex={-1}
        >
          Civic Expedition
        </h2>
        <p>
          A shared seven-day goal. Join first, then bring back your discoveries, restorations and
          craft.
        </p>
      </header>
      {safetyLocked && <p role="status">Expedition actions unlock when it is safe to play.</p>}
      {!board.enabled && <p role="status">Expeditions are paused in this realm.</p>}
      {board.isPending && <p role="status">Opening your Tribe’s expedition…</p>}
      {board.error && (
        <StatePanel kind="error" title="Expedition unavailable">
          We couldn’t check your shared progress. Your previous contributions are safe.
          <SecondaryActionButton onClick={() => void board.refetch()}>
            Try again
          </SecondaryActionButton>
        </StatePanel>
      )}
      {board.data && !board.error && !current && (
        <p>No expedition yet. A leader or officer can open the first one.</p>
      )}
      {board.data?.choices && !active && !board.error && (
        <CivicExpeditionChoices
          key={choiceFocus ?? 'catalog'}
          choices={
            choiceFocus
              ? [...board.data.choices].sort(
                  (a, b) =>
                    Number(b.template_code === choiceFocus) -
                    Number(a.template_code === choiceFocus),
                )
              : board.data.choices
          }
          canStart={board.data.can_start}
          disabled={disabled}
          safetyLocked={safetyLocked}
          onStart={(templateCode, branchCode) =>
            submit({
              type: 'start',
              templateCode,
              branchCode,
              commandId: commandId('expedition.start'),
            })
          }
        />
      )}
      {board.data?.can_start && !board.data.choices && !board.error && (
        <PrimaryActionButton
          disabled={disabled}
          loading={command.isPending}
          onClick={() => submit({ type: 'start', commandId: commandId('expedition.start') })}
        >
          Start a seven-day expedition
        </PrimaryActionButton>
      )}
      {current && (
        <StonePanel className="civic-expedition__contract" data-expedition-state={current.status}>
          <header>
            <h3>{current.title}</h3>
            {current.branch_title && <p>{current.branch_title}</p>}
            <strong role="status">
              {current.status === 'completed'
                ? 'Expedition complete'
                : current.status === 'expired'
                  ? 'Expedition ended'
                  : `${completedRoles} of 3 paths restored`}
            </strong>
            {active && (
              <small>
                Until{' '}
                {new Date(current.ends_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                · no location sharing
              </small>
            )}
          </header>
          {active && !current.enrolled && (
            <p>Join before you play. Only new achievements after joining count.</p>
          )}
          {nextRole && board.data?.choices && (
            <p className="civic-expedition__next" role="status">
              Your next step · {nextRole.title}:{' '}
              {nextRole.can_contribute
                ? 'Your achievement is ready. Contribute it below.'
                : nextRole.objective}
            </p>
          )}
          {current.can_join && (
            <PrimaryActionButton
              disabled={disabled}
              loading={command.isPending}
              onClick={() =>
                submit({
                  type: 'join',
                  expeditionId: current.id,
                  commandId: commandId(`expedition.join.${current.id}`),
                })
              }
            >
              Join this expedition
            </PrimaryActionButton>
          )}
          <ol className="civic-expedition__roles">
            {current.workstreams.map((role) => {
              const complete = role.current >= role.target;
              return (
                <li key={role.code} data-complete={complete || undefined}>
                  <div className="civic-expedition__role-heading">
                    <h4>{role.title}</h4>
                    <span>{complete ? 'Complete' : `${role.current} / ${role.target}`}</span>
                  </div>
                  <p>{role.objective}</p>
                  {!complete && role.requirement !== role.objective && (
                    <small>{role.requirement}</small>
                  )}
                  {active &&
                    current.enrolled &&
                    !complete &&
                    (role.can_contribute ? (
                      <PrimaryActionButton
                        disabled={disabled}
                        loading={command.isPending}
                        onClick={() =>
                          submit({
                            type: 'contribute',
                            expeditionId: current.id,
                            workstream: role.code,
                            commandId: commandId(`expedition.${current.id}.${role.code}`),
                          })
                        }
                      >
                        Contribute as {role.title}
                      </PrimaryActionButton>
                    ) : role.can_launch ? (
                      <PrimaryActionButton
                        disabled={disabled}
                        onClick={() =>
                          launch({
                            expeditionId: current.id,
                            workstream: role.code,
                            commandId: commandId(`expedition.challenge.${current.id}.${role.code}`),
                          })
                        }
                      >
                        {role.action_label ?? `Open ${role.title} challenge`}
                      </PrimaryActionButton>
                    ) : (
                      !safetyLocked &&
                      (role.prerequisite ? (
                        <small>Complete the {role.prerequisite} path first.</small>
                      ) : (
                        <Link className="civic-expedition__play" to={role.action_path}>
                          {role.action_label ??
                            (role.code === 'wayfinder'
                              ? 'Explore · outdoors or from home'
                              : role.code === 'scribe'
                                ? 'Open Decode'
                                : 'Open your settlement')}
                        </Link>
                      ))
                    ))}
                </li>
              );
            })}
          </ol>
          {current.status === 'completed' && (
            <div className="civic-expedition__outcome">
              <h4>{current.outcome?.title ?? 'Recorded in the city chronicle'}</h4>
              <p>
                {current.outcome?.description ??
                  'Your Tribe’s achievement is recorded in the city chronicle.'}
              </p>
            </div>
          )}
          {active && current.enrolled && (
            <p className="civic-expedition__hint">
              Work together or take several roles yourself. Each achievement can be contributed
              once.
            </p>
          )}
        </StonePanel>
      )}
      {history.length > 0 && (
        <section
          aria-labelledby="civic-expedition-history-title"
          className="civic-expedition__history"
        >
          <h3 id="civic-expedition-history-title">Recent expeditions</h3>
          <p>Earlier shared achievements stay recorded when a new expedition begins.</p>
          <ul>
            {history.map((expedition) => (
              <li key={expedition.id}>
                <strong>{expedition.title}</strong>
                {expedition.outcome && (
                  <p>
                    {expedition.outcome.title} · {expedition.outcome.description}
                  </p>
                )}
                <span>
                  {expedition.status === 'completed' ? 'Completed together' : 'Expedition ended'}
                </span>
                <time dateTime={expedition.completed_at ?? expedition.ends_at}>
                  {new Date(expedition.completed_at ?? expedition.ends_at).toLocaleDateString(
                    undefined,
                    {
                      month: 'short',
                      day: 'numeric',
                    },
                  )}
                </time>
              </li>
            ))}
          </ul>
        </section>
      )}
      {!active && !board.error && Boolean(board.data?.next_goals?.length) && (
        <section className="civic-expedition__next-goals" aria-label="Available next paths">
          <h3>Where next?</h3>
          {board.data!.next_goals!.slice(0, 3).map((goal) => (
            <div key={goal.code}>
              <h4>{goal.title}</h4>
              <p>{goal.description}</p>
              {!safetyLocked &&
                (goal.template_code && board.data?.can_start ? (
                  <SecondaryActionButton
                    disabled={disabled}
                    onClick={() => {
                      setChoiceFocus(goal.template_code!);
                      document
                        .getElementById('expedition-choice-title')
                        ?.scrollIntoView({ block: 'start' });
                    }}
                  >
                    Review this expedition
                  </SecondaryActionButton>
                ) : !goal.template_code && personalGoalLabels[goal.action_path] ? (
                  <Link className="civic-expedition__play" to={goal.action_path}>
                    {personalGoalLabels[goal.action_path]}
                  </Link>
                ) : (
                  <p>A leader or officer can choose this shared goal.</p>
                ))}
            </div>
          ))}
        </section>
      )}
      {challenge.error && (
        <div role="alert" className="inline-error">
          <p>{challenge.error.message}</p>
          <SecondaryActionButton
            disabled={!board.enabled || locked || safetyLocked || challenge.isPending}
            onClick={() => pendingChallenge.current && launch(pendingChallenge.current)}
          >
            Retry this challenge
          </SecondaryActionButton>
          <SecondaryActionButton
            onClick={() => {
              challenge.reset();
              pendingChallenge.current = null;
              void board.refetch();
            }}
          >
            Refresh challenge availability
          </SecondaryActionButton>
        </div>
      )}
      {command.error && (
        <div className="inline-error" role="alert">
          <p>{command.error.message}</p>
          <SecondaryActionButton
            disabled={!board.enabled || locked || safetyLocked || command.isPending}
            onClick={() => {
              if (pending.current) submit(pending.current);
            }}
          >
            Retry the same action
          </SecondaryActionButton>
          <SecondaryActionButton
            disabled={!board.enabled}
            onClick={() => {
              command.reset();
              pending.current = null;
              if (board.enabled) void board.refetch();
            }}
          >
            Refresh progress
          </SecondaryActionButton>
        </div>
      )}
      {command.isSuccess && <p role="status">Your expedition has been updated.</p>}
    </section>
  );
}
